import * as React from 'react';
import { Link, navigate, withPrefix } from 'gatsby';
import { createPortal } from 'react-dom';
import { x, css, up, createGlobalStyle } from '@xstyled/styled-components';
import { useDocSearchKeyboardEvents } from '@docsearch/react';
import { Input, InputGroup, InputGroupIcon } from './Input';

require('@docsearch/css');

const GlobalStyle = createGlobalStyle`
  /* Darkmode */
  body {
    --docsearch-modal-background: #F3F5F8;
    --docsearch-searchbox-shadow: 0 0 0 2px #0FA789;
    --docsearch-highlight-color: #0FA789;
    --docsearch-logo-color: #0FA789;
  }

  body.xstyled-color-mode-dark { 
    --docsearch-text-color: rgb(245, 246, 247);
    --docsearch-container-background:rgba(9, 10, 17, 0.8);
    --docsearch-modal-background: rgb(21, 23, 42);
    --docsearch-modal-shadow: inset 1px 1px 0 0 rgb(44, 46, 64),
      0 3px 8px 0 rgb(0, 3, 9);
    --docsearch-searchbox-background: rgb(9, 10, 17);
    --docsearch-searchbox-focus-background: #000;
    --docsearch-hit-color: rgb(190, 195, 201);
    --docsearch-hit-shadow: none;
    --docsearch-hit-background: rgb(9, 10, 17);
    --docsearch-key-gradient: linear-gradient(
      -26.5deg,
      rgb(86, 88, 114) 0%,
      rgb(49, 53, 91) 100%
    );
    --docsearch-key-shadow: inset 0 -2px 0 0 rgb(40, 45, 85),
      inset 0 0 1px 1px rgb(81, 87, 125), 0 2px 2px 0 rgba(3, 4, 9, 0.3);
    --docsearch-footer-background: rgb(30, 33, 54);
    --docsearch-footer-shadow: inset 0 1px 0 0 rgba(73, 76, 106, 0.5),
      0 -4px 8px 0 rgba(0, 0, 0, 0.2);
    --docsearch-logo-color: rgb(255, 255, 255);
    --docsearch-muted-color: rgb(127, 132, 151);
  }

  .DocSearch-Modal {
    ${up(
      'md',
      css`
        margin: 60px auto 60px auto;
        height: calc(100vh - 120px);
        display: flex;
      `
    )} 
  }

  .DocSearch-SearchBar {
    padding-bottom: 2px;
  } 
`;

function Hit({ hit, children }) {
  return <Link to={hit.url}>{children}</Link>;
}

let DocSearchModal = null;

export const DocSearch = ({ apiKey, indexName, appId }) => {
  const searchButtonRef = React.useRef(null);
  const [isShowing, setIsShowing] = React.useState(false);
  const [initialQuery, setInitialQuery] = React.useState(null);

  const importDocSearchModalIfNeeded = React.useCallback(() => {
    if (DocSearchModal) {
      return Promise.resolve();
    }

    return Promise.resolve(import('@docsearch/react/modal')).then(
      ({ DocSearchModal: Modal }) => {
        DocSearchModal = Modal;
      }
    );
  }, []);

  const onOpen = React.useCallback(() => {
    importDocSearchModalIfNeeded().then(() => {
      // We check that no other DocSearch modal is showing before opening this
      // one (we use one instance for desktop and one instance for mobile).
      if (document.body.classList.contains('DocSearch--active')) {
        return;
      }

      setIsShowing(true);
    });
  }, [importDocSearchModalIfNeeded, setIsShowing]);

  const onClose = React.useCallback(() => {
    setIsShowing(false);
  }, [setIsShowing]);

  const onInput = React.useCallback(
    event => {
      importDocSearchModalIfNeeded().then(() => {
        setIsShowing(true);
        setInitialQuery(event.key);
      });
    },
    [importDocSearchModalIfNeeded, setIsShowing, setInitialQuery]
  );

  useDocSearchKeyboardEvents({
    isOpen: isShowing,
    onOpen,
    onClose,
    onInput,
    searchButtonRef
  });

  return (
    <>
      <GlobalStyle />
      <x.div display={{ xs: 'none', lg: 'block' }}>
        <InputGroup>
          <Input
            ref={searchButtonRef}
            onClick={onOpen}
            type="search"
            placeholder="Search"
          />
          <InputGroupIcon>
            <svg
              width="17"
              height="17"
              viewBox="0 0 17 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.1517 10.6912H11.38L11.1117 10.4249C12.0594 9.31982 12.6358 7.8872 12.6358 6.31754C12.6358 2.82831 9.80732 0 6.3179 0C2.82847 0 0 2.82831 0 6.31754C0 9.80676 2.82847 12.6351 6.3179 12.6351C7.88765 12.6351 9.31938 12.0597 10.4245 11.113L10.6928 11.3793V12.1491L15.5508 17L17 15.5509L12.1517 10.6912ZM6.3179 10.6912C3.90154 10.6912 1.94397 8.73375 1.94397 6.31754C1.94397 3.90229 3.90154 1.94386 6.3179 1.94386C8.73328 1.94386 10.6918 3.90229 10.6918 6.31754C10.6918 8.73375 8.73328 10.6912 6.3179 10.6912Z"
                fill="#0FA789"
              />
            </svg>
          </InputGroupIcon>
        </InputGroup>
      </x.div>
      <x.button
        display={{ lg: 'none' }}
        css={{
          '&:focus': {
            outline: 'none'
          }
        }}
        background="transparent"
        onClick={onOpen}
        p="3"
      >
        <svg
          width="17"
          height="17"
          viewBox="0 0 17 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12.1517 10.6912H11.38L11.1117 10.4249C12.0594 9.31982 12.6358 7.8872 12.6358 6.31754C12.6358 2.82831 9.80732 0 6.3179 0C2.82847 0 0 2.82831 0 6.31754C0 9.80676 2.82847 12.6351 6.3179 12.6351C7.88765 12.6351 9.31938 12.0597 10.4245 11.113L10.6928 11.3793V12.1491L15.5508 17L17 15.5509L12.1517 10.6912ZM6.3179 10.6912C3.90154 10.6912 1.94397 8.73375 1.94397 6.31754C1.94397 3.90229 3.90154 1.94386 6.3179 1.94386C8.73328 1.94386 10.6918 3.90229 10.6918 6.31754C10.6918 8.73375 8.73328 10.6912 6.3179 10.6912Z"
            fill="#0FA789"
          />
        </svg>
      </x.button>

      {isShowing &&
        createPortal(
          <DocSearchModal
            apiKey={apiKey}
            indexName={indexName}
            appId={appId ?? 'BH4D9OD16A'}
            initialQuery={initialQuery}
            onClose={onClose}
            navigator={{
              navigate({ suggestionUrl }) {
                navigate(suggestionUrl);
              }
            }}
            transformItems={items => {
              return items.map(item => {
                const url = new URL(item.url);
                return {
                  ...item,
                  url: item.url.replace(`${url.origin}${withPrefix('/')}`, '/')
                };
              });
            }}
            hitComponent={Hit}
          />,
          document.body
        )}
    </>
  );
};
